import { BufferAttribute, BufferGeometry, Mesh } from 'three';
import {
  SDFGenerator,
  applySheetSlab,
  closeSDFInPlace,
  extractMesh,
  getSdfWorkerPool,
} from '@modules/three/common/voxels';
import {
  fillMinorBoundaryLoops,
  removeSmallComponents,
  smoothSurface,
  subdivideLongTriangles,
  weldMeshVertices,
} from './alignerMeshShell';
import { extrudeGumSkirt, selectRims } from './gumSkirt';
import { repairToManifold } from './seamFusion';

/**
 * The GENERATED part of the model — the wall running from the gum's rim down
 * to the base plane — as its own voxel-built body, for the hybrid export: the
 * anatomy keeps the exact mesh offset, and this body simply overlaps it near
 * the rim; the slicer unions the two.
 *
 * Voxels are the right tool exactly here and nowhere else. The rim is the one
 * region where the scan is junk (curled boundary slivers) and fidelity is
 * worthless (no anatomy, foil never forms down here) — and an SDF is blind to
 * junk, self-intersections and non-manifoldness alike, the same blindness
 * that disqualified it for the crowns. Every mesh-level cure for the skirt
 * junction — trimming boundary rows, sewing, fans — traded one artifact for
 * another on some jaw; a voxel hull has nothing to trade.
 *
 * The body = the curtain sheet (rim extruded to the plane) plus a `bandMm`
 * strip of the gum above it (so the overlap with the exact shell is solid),
 * given a `thicknessMm` slab, cut flat at the plane, extracted, smoothed.
 * Returns null when the gum has no rim long enough to skirt.
 */
export async function buildVoxelSkirtBody(
  gum: BufferGeometry,
  opens: 'down' | 'up',
  thicknessMm: number,
  // Coarse on purpose: this is the one body whose fidelity is worthless, and
  // at 0.15 mm the band came out with 400 k vertices — heavier than the
  // whole anatomical shell it accompanies.
  cellMm = 0.25,
  // Narrow on purpose: where the gum is shallow, a wide band reaches the
  // tooth sockets and its termination lands at the crown bases in plain
  // sight. 0.5 mm still overlaps the anatomy shell by three wall thicknesses.
  bandMm = 0.5,
): Promise<{ geometry: BufferGeometry; planeY: number; skirted: number } | null> {
  const rimsBefore = selectRims(gum, 60);
  if (rimsBefore.length === 0) return null;
  const oldFaceCount = gum.getIndex()!.count / 3;
  const oldVertexCount = gum.getAttribute('position').count;

  // No trim: the whole point of the voxel route is that the curled boundary
  // junk the mesh path had to cut away simply drowns in the field.
  const skirt = extrudeGumSkirt(gum, opens, 1, 12, 60, 0);
  if (skirt.skirted === 0) return null;

  // Edge-relaxed distance from the rim; faces within the band join the body
  // so it overlaps the exact shell by a solid margin.
  const position = gum.getAttribute('position') as BufferAttribute;
  const index = gum.getIndex()!;
  const vertexCount = position.count;
  const array = position.array as Float32Array;
  const dist = new Float32Array(vertexCount).fill(Infinity);
  for (const rim of rimsBefore) for (const v of rim.walk) dist[v] = 0;
  for (let pass = 0; pass < 48; pass++) {
    let changed = false;
    for (let i = 0; i < index.count; i += 3) {
      for (let e = 0; e < 3; e++) {
        const a = index.getX(i + e);
        const b = index.getX(i + ((e + 1) % 3));
        const length = Math.hypot(
          array[a * 3] - array[b * 3],
          array[a * 3 + 1] - array[b * 3 + 1],
          array[a * 3 + 2] - array[b * 3 + 2],
        );
        if (dist[a] + length < dist[b]) {
          dist[b] = dist[a] + length;
          changed = true;
        } else if (dist[b] + length < dist[a]) {
          dist[a] = dist[b] + length;
          changed = true;
        }
      }
    }
    if (!changed) break;
  }

  const band: number[] = [];
  for (let f = 0; f < index.count / 3; f++) {
    const near =
      f >= oldFaceCount ||
      dist[index.getX(f * 3)] <= bandMm ||
      dist[index.getX(f * 3 + 1)] <= bandMm ||
      dist[index.getX(f * 3 + 2)] <= bandMm;
    if (!near) continue;
    band.push(index.getX(f * 3), index.getX(f * 3 + 1), index.getX(f * 3 + 2));
  }

  // Wherever the band ends — its upper boundary, and the socket rims it
  // reaches when the gum is shallow — the slab terminates in a flush cut of
  // coarse voxels, and that cut used to sit ON the visible surface: a chain
  // of angular beads around every crown base (the user's screenshot). Tuck
  // those boundary vertices UNDER the surface before voxelising, so the body
  // emerges from inside the anatomy shell and its terminations are buried.
  // The outer rim (distance ~0) and the curtain's bottom ring stay put.
  {
    const use = new Map<number, number>();
    for (let i = 0; i < band.length; i += 3) {
      for (let e = 0; e < 3; e++) {
        const a = band[i + e];
        const b = band[i + ((e + 1) % 3)];
        const key = a < b ? a * vertexCount + b : b * vertexCount + a;
        use.set(key, (use.get(key) ?? 0) + 1);
      }
    }
    const boundary = new Set<number>();
    use.forEach((count, key) => {
      if (count !== 1) return;
      const a = Math.floor(key / vertexCount);
      boundary.add(a);
      boundary.add(key - a * vertexCount);
    });

    // Area-weighted outward normals of the gum, oriented by the enclosed
    // tetra volume — a cup wound outwards encloses positive volume.
    const normals = new Float64Array(oldVertexCount * 3);
    let cx = 0;
    let cy = 0;
    let cz = 0;
    for (let v = 0; v < oldVertexCount; v++) {
      cx += array[v * 3];
      cy += array[v * 3 + 1];
      cz += array[v * 3 + 2];
    }
    cx /= oldVertexCount;
    cy /= oldVertexCount;
    cz /= oldVertexCount;
    let enclosed = 0;
    for (let f = 0; f < oldFaceCount; f++) {
      const a = index.getX(f * 3) * 3;
      const b = index.getX(f * 3 + 1) * 3;
      const c = index.getX(f * 3 + 2) * 3;
      const ux = array[b] - array[a];
      const uy = array[b + 1] - array[a + 1];
      const uz = array[b + 2] - array[a + 2];
      const vx = array[c] - array[a];
      const vy = array[c + 1] - array[a + 1];
      const vz = array[c + 2] - array[a + 2];
      const nx = uy * vz - uz * vy;
      const ny = uz * vx - ux * vz;
      const nz = ux * vy - uy * vx;
      for (const corner of [a, b, c]) {
        normals[corner] += nx;
        normals[corner + 1] += ny;
        normals[corner + 2] += nz;
      }
      enclosed += (array[a] - cx) * nx + (array[a + 1] - cy) * ny + (array[a + 2] - cz) * nz;
    }
    const sign = enclosed < 0 ? -1 : 1;
    const tuckMm = 0.6;
    for (const v of boundary) {
      if (v >= oldVertexCount || dist[v] <= 0.5) continue;
      const length = Math.hypot(normals[v * 3], normals[v * 3 + 1], normals[v * 3 + 2]);
      if (length < 1e-12) continue;
      const push = (sign * tuckMm) / length;
      array[v * 3] -= normals[v * 3] * push;
      array[v * 3 + 1] -= normals[v * 3 + 1] * push;
      array[v * 3 + 2] -= normals[v * 3 + 2] * push;
    }
  }

  const sheet = new BufferGeometry();
  sheet.setAttribute('position', new BufferAttribute(array, 3));
  sheet.setIndex(band);

  // A marching-cubes wall thinner than ~3 cells cracks; the skirt may be
  // thicker than asked, never thinner than printable.
  const slabMm = Math.max(thicknessMm, 3 * cellMm);
  const soup = subdivideLongTriangles(sheet, 1.5);
  const generator = new SDFGenerator([new Mesh(soup)]);
  const grid = await generator.generateNarrowBandFlood(
    {
      cellSize: cellMm,
      padding: 1.5,
      narrowBandMm: slabMm + 6 * cellMm,
      openSheet: true,
    },
    getSdfWorkerPool(),
  );
  generator.dispose();
  soup.dispose();
  sheet.dispose();
  closeSDFInPlace(grid, 1, '26-connected');
  applySheetSlab(grid, slabMm);

  // Flat base exactly at the skirt plane.
  const { origin, counts, strides, cellSize } = grid.info;
  const data = grid.data;
  for (let x = 0; x < counts.x; x++) {
    for (let y = 0; y < counts.y; y++) {
      const wy = origin.y + y * cellSize;
      const sd = opens === 'down' ? skirt.planeY - wy : wy - skirt.planeY;
      if (sd <= -cellSize) continue;
      let idx = x * strides.x + y * strides.y;
      for (let z = 0; z < counts.z; z++, idx++) {
        if (sd > data[idx]) data[idx] = sd;
      }
    }
  }

  const raw = extractMesh(grid, { computeNormals: false });
  const welded = weldMeshVertices(raw, cellSize * 0.05);
  raw.dispose();
  for (let pass = 0; pass < 6; pass++) {
    if (fillMinorBoundaryLoops(welded, 0) === 0) break;
  }
  removeSmallComponents(welded, 200);
  // What the crack fill cannot close is marching cubes' non-manifold residue
  // (face-ambiguity fins, edges owned three times); the manifold repair was
  // built for exactly that census.
  repairToManifold(welded, 0, 6);
  smoothSurface(welded, 8, 0.5, -0.53, slabMm / 6);

  // Marching cubes emits inward winding for our negative-inside field.
  const wound = welded.getIndex()!;
  for (let i = 0; i < wound.count; i += 3) {
    const tmp = wound.getX(i + 1);
    wound.setX(i + 1, wound.getX(i + 2));
    wound.setX(i + 2, tmp);
  }
  return { geometry: welded, planeY: skirt.planeY, skirted: skirt.skirted };
}
